import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useNavigation } from '../../utilities/NavigationContext';
import { useAuth } from '../../utilities/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AgreementModal from './AgreementModal';

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

class Login extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      showAgreementModal: false,
      pendingAgreements: [],
      currentAgreement: null,
      tempAuthData: null,
      emailNotVerified: false,
      loading: false,
    };
  }

  componentDidMount() {
    if (this.props.auth?.isLoggedIn) {
      this.props.navigate('/planning');
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.auth?.isLoggedIn && this.props.auth?.isLoggedIn) {
      if (this.state.showAgreementModal) return;
      this.props.navigate('/planning');
    }
  }

  handleInputChange = (event) => {
    const { name, value } = event.target;
    this.setState({
      [name]: value,
      ...(name === 'email' ? { emailNotVerified: false } : {}),
    });
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    const { email, password } = this.state;
    const loginEndpoint = `${EndPoint}/user/login`;

    this.setState({ loading: true });

    try {
      const response = await axios.post(
        loginEndpoint,
        { email, password },
        { withCredentials: true }
      );

      if (response.status === 200) {
        toast.success('Login success!', {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
          hideProgressBar: false,
        });

        const backendPending = response.data.pendingAgreements || [];
        const userAgreements = response.data.agreement || [];

        let finalPending = backendPending;
        if ((!backendPending || backendPending.length === 0) && userAgreements.length > 0) {
          const needsConsent = userAgreements.filter((ua) => ua.consent === 'no');
          finalPending = needsConsent.map((ua) => ({
            _id: ua.agreementId,
            title: 'User Agreement',
            content: 'You have a new user agreement to review. Please accept to continue.',
          }));
        }

        localStorage.removeItem('patientData');

        if (finalPending.length > 0) {
          const authPayload = {
            isLoggedIn: true,
            credits: response.data.credits,
            role: response.data.role,
            userId: response.data.userId,
            hasSubscription: response.data.hasSubscription || false,
          };

          this.setState({
            showAgreementModal: true,
            pendingAgreements: finalPending,
            currentAgreement: finalPending[0],
            tempAuthData: authPayload,
            loading: false,
          });
          return;
        }

        this.props.setAuth((prevAuth) => ({
          ...prevAuth,
          isLoggedIn: true,
          credits: response.data.credits,
          role: response.data.role,
          userId: response.data.userId,
          hasSubscription: response.data.hasSubscription || false,
        }));

        this.setState({ loading: false });
        this.props.navigate('/planning');
      } else {
        toast.error('Login failed!', {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
          hideProgressBar: false,
        });
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error(error);

      if (
        error.response?.status === 401 &&
        error.response?.data?.error === 'email_not_verified'
      ) {
        this.setState({
          emailNotVerified: true,
          loading: false,
        });

        toast.warn(
          error.response?.data?.message || 'Email is not verified yet.',
          {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 4000,
            hideProgressBar: false,
          }
        );
        return;
      }

      toast.error(
        error?.response?.data?.message || 'There was an error during login!',
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2500,
          hideProgressBar: false,
        }
      );

      this.setState({ loading: false });
    }
  };

  handleAgreementConsent = async (consent) => {
    const { currentAgreement, pendingAgreements, tempAuthData } = this.state;
    if (!currentAgreement) return;

    if (currentAgreement._id) {
      try {
        await axios.post(
          `${EndPoint}/user/accept-agreement`,
          {
            agreementId: currentAgreement._id,
            consent,
          },
          { withCredentials: true }
        );
      } catch (err) {
        console.error('Error updating agreement consent', err);
        toast.error('Error saving agreement consent', {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }

    const remaining = pendingAgreements.filter(
      (ag) => ag._id !== currentAgreement._id
    );

    if (remaining.length > 0) {
      this.setState({
        pendingAgreements: remaining,
        currentAgreement: remaining[0],
        showAgreementModal: true,
      });
    } else {
      this.setState({
        pendingAgreements: [],
        currentAgreement: null,
        showAgreementModal: false,
      });

      if (tempAuthData) {
        this.props.setAuth((prevAuth) => ({
          ...prevAuth,
          ...tempAuthData,
        }));
      }

      this.props.navigate('/planning');
    }
  };

  resendEmail = async (e) => {
    if (e) e.preventDefault();

    const { email } = this.state;

    if (!email) {
      toast.error('Please enter your email first.', {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    try {
      await axios.post(
        `${EndPoint}/user/resend-verification`,
        { email },
        { withCredentials: true }
      );

      toast.success('Verification email has been resent.', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });

      this.setState({ emailNotVerified: false });
    } catch (err) {
      console.error('Resend verification error:', err);

      toast.error(
        err.response?.data?.message || 'Unable to resend verification email.',
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        }
      );
    }
  };

  render() {
    const {
      email,
      password,
      showAgreementModal,
      currentAgreement,
      emailNotVerified,
      loading,
    } = this.state;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-sky-950 to-cyan-900">
        <ToastContainer />

        <div className="mx-auto flex min-h-screen max-w-7xl items-center px-4 py-10">
          <div className="grid w-full overflow-hidden rounded-[32px] border border-white/10 bg-white/10 shadow-2xl backdrop-blur-xl lg:grid-cols-2">
            {/* Left panel */}
            <div className="relative hidden overflow-hidden bg-gradient-to-br from-sky-900 via-cyan-800 to-slate-900 p-10 lg:flex lg:flex-col lg:justify-between">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(56,189,248,0.2),transparent_30%)]" />
              <div className="relative z-10">
                <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-100">
                  Caredevo
                </div>
                <h1 className="mt-6 text-4xl font-bold leading-tight text-white">
                  Welcome back
                </h1>
                <p className="mt-4 max-w-md text-sm leading-7 text-sky-100/90">
                  Log in to continue building care plans, reviewing workflows,
                  and accessing your Caredevo tools.
                </p>
              </div>

              <div className="relative z-10 grid gap-4">
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-semibold text-white">Secure access</p>
                  <p className="mt-1 text-sm text-sky-100/80">
                    Login respects agreement checks and verified email status.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-semibold text-white">Continue where you left off</p>
                  <p className="mt-1 text-sm text-sky-100/80">
                    Pick up planning and clinical work without losing your flow.
                  </p>
                </div>
              </div>
            </div>

            {/* Right panel */}
            <div className="bg-white px-6 py-10 sm:px-10 lg:px-12">
              <div className="mx-auto w-full max-w-md">
                <div className="mb-8 lg:hidden">
                  <div className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
                    Caredevo
                  </div>
                </div>

                <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                  Login
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Enter your email and password to access your account.
                </p>

                <form onSubmit={this.handleSubmit} className="mt-8 space-y-5">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={this.handleInputChange}
                      required
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={this.handleInputChange}
                      required
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                    />
                  </div>

                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-slate-500">Forgot your password?</span>
                    <button
                      type="button"
                      onClick={() => this.props.navigate('/forgotpassword')}
                      className="font-semibold text-sky-700 transition hover:text-sky-900"
                    >
                      Reset here
                    </button>
                  </div>

                  {emailNotVerified && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      Your email is not verified yet. Please verify your email,
                      or resend the verification link below.
                    </div>
                  )}

                  <div className="pt-2">
                    {emailNotVerified ? (
                      <button
                        type="button"
                        onClick={this.resendEmail}
                        className="w-full rounded-2xl bg-amber-500 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-600"
                      >
                        Resend Verification Email
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={loading}
                        className={`w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white shadow-sm transition ${
                          loading
                            ? 'cursor-not-allowed bg-slate-400'
                            : 'bg-sky-700 hover:bg-sky-800'
                        }`}
                      >
                        {loading ? 'Logging in...' : 'Login'}
                      </button>
                    )}
                  </div>
                </form>

                <div className="mt-8 border-t border-slate-200 pt-6 text-center text-sm text-slate-500">
                  Don&apos;t have an account?{' '}
                  <Link
                    to="/register"
                    className="font-semibold text-sky-700 transition hover:text-sky-900"
                  >
                    Click here to register
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <AgreementModal
          isOpen={showAgreementModal}
          agreement={currentAgreement}
          onConsent={this.handleAgreementConsent}
        />
      </div>
    );
  }
}

const LoginWithNavigation = (props) => {
  const navigate = useNavigation();
  const [auth, setAuth] = useAuth();

  return (
    <Login
      {...props}
      navigate={navigate}
      auth={auth}
      setAuth={setAuth}
    />
  );
};

export default LoginWithNavigation;